import { Button } from '@shared/components/ui/button';
import { Input } from '@shared/components/ui/input';
import { Label } from '@shared/components/ui/label';
import { ModalCenter, ModalCenterRef } from '@shared/components/ui/ModalCenter';
import { Product as ProductInfo } from '@shared/components/ui/Product';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@shared/components/ui/select';
import { faQrcode } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import JsBarcode from 'jsbarcode';
import jsPDF from 'jspdf';
import * as QRCode from 'qrcode';
import { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

export interface ProductBarcodeModalData {
  id: number;
  name: string;
  code: string;
  image?: string;
}

export interface BarcodeModalRef {
  open: (product: ProductBarcodeModalData) => void;
}

interface BarcodeOptions {
  format: string;
  width: number;
  height: number;
  displayValue: boolean;
  fontSize: number;
  copies: number;
  customWidth: number;
  customHeight: number;
  margin: number;
}

const BARCODE_FORMATS = [
  { value: 'CODE128', label: 'CODE128 (Recomendado)' },
  { value: 'QR', label: 'Código QR' },
  { value: 'CODE39', label: 'CODE39' },
  { value: 'EAN13', label: 'EAN13' },
  { value: 'EAN8', label: 'EAN8' },
  { value: 'UPC', label: 'UPC' },
  { value: 'ITF14', label: 'ITF14' },
  { value: 'pharmacode', label: 'Pharmacode' },
  { value: 'codabar', label: 'Codabar' },
];

const LABEL_SIZES = [
  { width: 40, height: 20, label: '40×20 mm' },
  { width: 50, height: 25, label: '50×25 mm' },
  { width: 60, height: 30, label: '60×30 mm' },
  { width: 70, height: 40, label: '70×40 mm' },
];

export const BarcodeModal = forwardRef<BarcodeModalRef>((_, ref) => {
  const { t } = useTranslation('productsModule');
  const modalRef = useRef<ModalCenterRef>(null);
  const [product, setProduct] = useState<ProductBarcodeModalData | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);

  const [options, setOptions] = useState<BarcodeOptions>({
    format: 'CODE128',
    width: 2, // Se calcula automáticamente, pero mantenemos para compatibilidad
    height: 100, // Se calcula automáticamente, pero mantenemos para compatibilidad
    displayValue: true,
    fontSize: 20,
    copies: 1,
    customWidth: 40,
    customHeight: 20,
    margin: 10,
  });

  useImperativeHandle(ref, () => ({
    open: (productData: ProductBarcodeModalData) => {
      setProduct(productData);
      modalRef.current?.openModal();
    },
  }));

  const generatePDF = async () => {
    if (!product) return;

    setIsGenerating(true);

    try {
      const pageConfig = { width: options.customWidth, height: options.customHeight };

      // Determinar orientación basada en las dimensiones
      const orientation = pageConfig.width > pageConfig.height ? 'landscape' : 'portrait';

      const pdf = new jsPDF({
        orientation: orientation,
        unit: 'mm',
        format: [pageConfig.width, pageConfig.height],
      });

      // Calcular dimensiones automáticamente (necesario para ambos tipos de código)
      const calculatedSettings = calculateBarcodeSettings();

      // Generar imagen del código (QR o código de barras)
      let codeImage;

      if (options.format === 'QR') {
        // Generar código QR
        const qrSize = Math.min(pageConfig.width, pageConfig.height) * 10; // Tamaño en píxeles para alta resolución
        codeImage = await QRCode.toDataURL(product.code, {
          width: qrSize,
          margin: 1,
          color: {
            dark: '#000000',
            light: '#FFFFFF',
          },
        });
      } else {
        // Generar código de barras tradicional
        const tempCanvas = document.createElement('canvas');

        // Ajustar las opciones del código de barras según las dimensiones de la página
        const barcodeOptions = {
          format: options.format,
          width: calculatedSettings.width,
          height: calculatedSettings.height,
          displayValue: false, // No mostrar texto en el código, lo agregaremos después
          fontSize: calculatedSettings.fontSize,
          margin: 2, // Margen mínimo en el código
          background: '#ffffff',
          lineColor: '#000000',
        };

        // Configurar el código de barras
        JsBarcode(tempCanvas, product.code, barcodeOptions);
        codeImage = tempCanvas.toDataURL('image/png');
      }

      // Usar las dimensiones exactas de la página menos márgenes mínimos
      const margin = 1; // 1mm de margen mínimo
      const textSpace = options.displayValue ? calculatedSettings.fontSize * 1.3 : 0;

      // Distribución fija: 75% código, 25% texto
      const totalHeight = pageConfig.height - 2 * margin;
      let barcodeWidth = pageConfig.width - 2 * margin;
      let barcodeHeight = options.displayValue ? totalHeight * 0.75 : totalHeight;
      const textAreaHeight = options.displayValue ? totalHeight * 0.25 : 0;

      // Posición inicial
      let x = margin;
      const y = margin;

      // Para códigos QR, hacer dimensiones cuadradas y centrar
      if (options.format === 'QR') {
        const qrSize = Math.min(barcodeWidth, barcodeHeight);
        barcodeWidth = qrSize;
        barcodeHeight = qrSize;
        // Centrar el QR horizontalmente
        x = margin + (pageConfig.width - 2 * margin - qrSize) / 2;
      }

      // Generar una página por cada copia
      for (let i = 0; i < options.copies; i++) {
        // Agregar nueva página si no es la primera
        if (i > 0) {
          pdf.addPage();
        }

        // Verificar que las coordenadas sean válidas
        if (x >= 0 && y >= 0 && barcodeWidth > 0 && barcodeHeight > 0) {
          // Agregar código (QR o código de barras)
          pdf.addImage(codeImage, 'PNG', x, y, barcodeWidth, barcodeHeight);

          // Agregar información del producto debajo del código
          if (options.displayValue && textSpace > 0) {
            pdf.setFontSize(calculatedSettings.fontSize);

            // Posicionar el texto centrado en su área del 25%
            const textAreaStart = y + barcodeHeight;
            const textY = textAreaStart + textAreaHeight / 2 + calculatedSettings.fontSize / 4; // Centrado verticalmente

            // Centrar el texto horizontalmente
            const textWidth = pdf.getTextWidth(product.code);
            const textX = (pageConfig.width - textWidth) / 2;

            pdf.text(product.code, Math.max(margin, textX), textY);
          }
        }
      }

      // Generar y descargar el PDF
      const fileName = `codigo_barras_${product.code}_${Date.now()}.pdf`;
      pdf.save(fileName);
    } catch (error) {
      console.error('Error generando PDF:', error);
    } finally {
      setIsGenerating(false);
    }
  };

  const updateOption = <K extends keyof BarcodeOptions>(key: K, value: BarcodeOptions[K]) => {
    setOptions((prev) => ({ ...prev, [key]: value }));
  };

  // Calcular automáticamente las dimensiones del código de barras y texto
  const calculateBarcodeSettings = () => {
    const pageConfig = { width: options.customWidth, height: options.customHeight };

    // Calcular tamaño de fuente automáticamente (entre 8 y 20 puntos)
    // Basado en el ancho de la página para que el texto sea legible y más grande
    const optimalFontSize = Math.max(8, Math.min(20, pageConfig.width / 5));

    // Distribución fija: 75% para código, 25% para texto
    const settingsAvailableHeight = pageConfig.height - 4; // 4mm de margen total
    const barcodeAreaHeight = options.displayValue ? settingsAvailableHeight * 0.75 : settingsAvailableHeight;

    // Para códigos QR, usar dimensiones cuadradas
    if (options.format === 'QR') {
      const qrSize = Math.min(pageConfig.width - 4, barcodeAreaHeight); // Cuadrado que quepa en el espacio
      return {
        width: 2, // No se usa para QR, but mantenemos para compatibilidad
        height: qrSize,
        fontSize: optimalFontSize,
      };
    }

    // Para códigos de barras tradicionales
    const optimalWidth = Math.max(1, Math.min(4, pageConfig.width / 25));
    const optimalHeight = Math.max(20, Math.min(barcodeAreaHeight, 120));

    return {
      width: optimalWidth,
      height: optimalHeight,
      fontSize: optimalFontSize,
    };
  };

  return (
    <ModalCenter
      ref={modalRef}
      title={t('barcode_generator')}
      icon={<FontAwesomeIcon icon={faQrcode} className="w-3 h-3" />}
      type="default"
      width="lg"
      className="h-[100dvh] sm:h-auto w-full sm:w-[480px]"
    >
      {product && (
        <div className="space-y-6 pt-3 pb-2">
          {/* Información del producto */}
          <ProductInfo
            id={product.id}
            name={product.name}
            src={product.image || ''}
            code={product.code}
            showImageModal={false}
          />

          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-foreground">{t('barcode_options')}</h3>

            {/* Formato del código */}
            <div className="space-y-2">
              <Label htmlFor="format">{t('barcode_format')}</Label>
              <Select value={options.format} onValueChange={(value) => updateOption('format', value)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {BARCODE_FORMATS.map((format) => (
                    <SelectItem key={format.value} value={format.value}>
                      {format.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Mostrar valor */}
            <div className="flex items-center space-x-2">
              <input
                id="displayValue"
                type="checkbox"
                checked={options.displayValue}
                onChange={(e) => updateOption('displayValue', e.target.checked)}
                className="rounded border-border"
              />
              <Label htmlFor="displayValue">{t('show_text')}</Label>
            </div>

            {/* Número de copias */}
            <div className="space-y-2">
              <Label htmlFor="copies">{t('number_of_copies')}</Label>
              <Input
                id="copies"
                type="number"
                min="1"
                max="100"
                value={options.copies}
                onChange={(e) => updateOption('copies', Number(e.target.value))}
              />
            </div>

            {/* Tamaño de etiqueta */}
            <div className="space-y-4 p-3 bg-muted/30 rounded-lg border border-border">
              <h4 className="text-sm font-medium text-foreground">{t('custom_dimensions')}</h4>
              <div className="space-y-2">
                <p className="text-xs font-medium text-foreground">{t('quick_sizes')}:</p>
                <div className="flex flex-wrap gap-2">
                  {LABEL_SIZES.map((size) => (
                    <button
                      key={`${size.width}x${size.height}`}
                      type="button"
                      onClick={() => {
                        updateOption('customWidth', size.width);
                        updateOption('customHeight', size.height);
                      }}
                      className={`px-2 py-1 text-xs rounded border transition-colors font-medium ${
                        options.customWidth === size.width && options.customHeight === size.height
                          ? 'bg-primary/20 border-primary/50 text-primary'
                          : 'bg-muted hover:bg-muted/80 border-border text-foreground'
                      }`}
                    >
                      {size.label}
                    </button>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3 pt-2 border-t border-border/50">
                <div className="space-y-2">
                  <Label htmlFor="customWidth">{t('width')} (mm)</Label>
                  <Input
                    id="customWidth"
                    type="number"
                    min="20"
                    max="150"
                    value={options.customWidth}
                    onChange={(e) => updateOption('customWidth', Number(e.target.value))}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="customHeight">{t('height_page')} (mm)</Label>
                  <Input
                    id="customHeight"
                    type="number"
                    min="20"
                    max="150"
                    value={options.customHeight}
                    onChange={(e) => updateOption('customHeight', Number(e.target.value))}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Botones de acción */}
          <div className="flex justify-end space-x-3 pt-4 border-t">
            <Button variant="secondary" onClick={() => modalRef.current?.closeModal()}>
              {t('buttons.cancel')}
            </Button>
            <Button variant="primary" onClick={generatePDF} disabled={isGenerating}>
              {isGenerating ? t('generating_pdf') : t('generate_pdf')}
            </Button>
          </div>
        </div>
      )}
    </ModalCenter>
  );
});

BarcodeModal.displayName = 'BarcodeModal';
